import { useEffect, useRef, useState } from 'react';
import type {
  AudioSegment,
  Exposure,
  PlaybackContext,
} from '../components/NarrationPlayer.tsx';

type Source =
  | { kind: 'lesson'; segment: AudioSegment }
  | { kind: 'reply'; url: string; text: string };

/** One audio element owns the tutor's voice. Interruptions snapshot the lesson
 * synchronously, before a question can enter the serialized server queue. */
export function useTutorVoice({
  onExposure,
  onLessonEnd,
}: {
  onExposure: (event: Exposure) => void;
  onLessonEnd: (id: string) => void;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const sourceRef = useRef<Source | undefined>(undefined);
  const savedLesson = useRef<
    { segment: AudioSegment; positionMs: number } | undefined
  >(undefined);
  const rangeStart = useRef<number | null>(null);
  const callbacks = useRef({ onExposure, onLessonEnd });
  callbacks.current = { onExposure, onLessonEnd };
  const [source, setSource] = useState<Source>();
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [error, setError] = useState('');
  const [rate, setRate] = useState(0.9);
  const generation = useRef(0);

  function snapshot(): PlaybackContext | undefined {
    const s = sourceRef.current;
    if (s?.kind === 'lesson' && audioRef.current) {
      const positionMs = Math.min(
        s.segment.durationMs,
        Math.round(audioRef.current.currentTime * 1000),
      );
      savedLesson.current = { segment: s.segment, positionMs };
      return {
        segmentId: s.segment.id,
        positionMs,
        playing: !audioRef.current.paused,
      };
    }
    const saved = savedLesson.current;
    return saved
      ? {
          segmentId: saved.segment.id,
          positionMs: saved.positionMs,
          playing: false,
        }
      : undefined;
  }
  function flush(event: Exposure['event']) {
    const s = sourceRef.current;
    const a = audioRef.current;
    const from = rangeStart.current;
    rangeStart.current = null;
    if (s?.kind !== 'lesson' || !a) return;
    const to = Math.min(s.segment.durationMs, Math.round(a.currentTime * 1000));
    snapshot();
    if (from !== null && to > from)
      callbacks.current.onExposure({
        audioId: s.segment.id,
        event,
        positionMs: to,
        fromMs: from,
        toMs: to,
        durationMs: s.segment.durationMs,
      });
    else if (event === 'skipped' || event === 'replayed')
      callbacks.current.onExposure({
        audioId: s.segment.id,
        event,
        positionMs: to,
      });
  }
  function pause() {
    generation.current++;
    audioRef.current?.pause();
    flush('stopped');
    setPlaying(false);
    return snapshot();
  }
  async function play() {
    if (!audioRef.current || !sourceRef.current) return;
    setError('');
    const requested = ++generation.current;
    try {
      await audioRef.current.play();
    } catch {
      if (requested === generation.current)
        setError('Tap Play to hear the tutor, or open the transcript to read.');
    }
  }
  function load(next: Source, autoplay: boolean, at = 0) {
    pause();
    sourceRef.current = next;
    setSource(next);
    setPosition(at);
    setError('');
    const a = audioRef.current;
    if (!a) return;
    a.src = next.kind === 'lesson' ? next.segment.url : next.url;
    a.playbackRate = rate;
    a.currentTime = at / 1000;
    if (next.kind === 'lesson')
      savedLesson.current = { segment: next.segment, positionMs: at };
    if (autoplay) void play();
  }
  function lesson(segment: AudioSegment | undefined, autoplay = false, at = 0) {
    if (!segment) {
      clear();
      setError(
        'Voice is unavailable for this part. Open the transcript to read it.',
      );
      return;
    }
    load({ kind: 'lesson', segment }, autoplay, at);
  }
  function seek(at: number) {
    const s = sourceRef.current;
    const a = audioRef.current;
    if (!a || !s) return;
    flush('stopped');
    const max =
      s.kind === 'lesson'
        ? s.segment.durationMs
        : Number.isFinite(a.duration)
          ? a.duration * 1000
          : 0;
    a.currentTime = Math.max(0, Math.min(at, max)) / 1000;
    setPosition(Math.round(a.currentTime * 1000));
    if (!a.paused) rangeStart.current = Math.round(a.currentTime * 1000);
    snapshot();
  }
  function resumeLesson() {
    const saved = savedLesson.current;
    if (!saved) return;
    // Resume at the preceding authored sentence, so a mid-word interruption
    // never makes the explanation restart in the middle of a thought.
    const at =
      saved.segment.checkpoints
        .filter((c) => c.startMs <= saved.positionMs)
        .at(-1)?.startMs ?? 0;
    lesson(saved.segment, true, at);
  }
  function skip() {
    pause();
    flush('skipped');
  }
  function clear() {
    pause();
    sourceRef.current = undefined;
    savedLesson.current = undefined;
    setSource(undefined);
    setPosition(0);
    setError('');
    audioRef.current?.removeAttribute('src');
  }
  useEffect(
    () => () => {
      audioRef.current?.pause();
    },
    [],
  );

  const audioProps = {
    ref: audioRef,
    preload: 'metadata',
    onPlay: () => {
      setPlaying(true);
      rangeStart.current = Math.round(
        (audioRef.current?.currentTime ?? 0) * 1000,
      );
    },
    onPause: () => {
      setPlaying(false);
      flush('stopped');
    },
    onTimeUpdate: () => {
      setPosition(Math.round((audioRef.current?.currentTime ?? 0) * 1000));
      snapshot();
    },
    onEnded: () => {
      const ended = sourceRef.current;
      flush('played');
      setPlaying(false);
      if (ended?.kind === 'lesson')
        callbacks.current.onLessonEnd(ended.segment.id);
    },
    onError: () => {
      setPlaying(false);
      flush('stopped');
      setError(
        'This audio could not load. Read the transcript or try Play again.',
      );
    },
  };
  return {
    audioProps,
    playing,
    position,
    source,
    error,
    rate,
    setRate: (next: number) => {
      setRate(next);
      if (audioRef.current) audioRef.current.playbackRate = next;
    },
    clearError: () => setError(''),
    pause,
    play,
    lesson,
    seek,
    skip,
    clear,
    resumeLesson,
    reply: (url: string, text: string) =>
      load({ kind: 'reply', url, text }, true),
    replay: () => {
      flush('replayed');
      seek(0);
      void play();
    },
    snapshot,
  };
}
